import React, { useState, useEffect } from "react";
import { ExternalLink, ShoppingBag, ArrowRight, Package, Lock } from "lucide-react";
import axios from "axios";
import { useSettings } from "../context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_URL = process.env.REACT_APP_PRINTIFY_STORE_URL;

const ProductCard = ({ product }) => (
    <div
        className="bg-white border border-[#D5D0C7] hover:border-[#3D2008] hover:shadow-[0_6px_20px_rgba(61,32,8,0.12)] transition-all duration-200 flex flex-col group"
        data-testid={`merch-card-${product.id}`}
    >
        <a href={product.link} target="_blank" rel="noopener noreferrer"
            className="block overflow-hidden aspect-square bg-[#F5EDD6]">
            <img src={product.image} alt={product.name} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </a>
        <div className="p-5 flex flex-col flex-1">
            <span className="font-manrope text-[10px] font-bold uppercase tracking-[0.14em] text-[#8A7A65] mb-1">
                {product.category}
            </span>
            <h3 className="font-anton text-lg text-[#3D2008] uppercase leading-tight mb-2 flex-1">
                {product.name}
            </h3>
            <div className="flex items-center justify-between mt-3">
                <span className="font-manrope font-bold text-xl text-[#3D2008]">{product.price}</span>
                <a href={product.link} target="_blank" rel="noopener noreferrer"
                    data-testid={`buy-btn-${product.id}`}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#3D2008] text-[#F5EDD6] font-manrope font-semibold text-sm hover:bg-[#C4522A] transition-colors duration-200">
                    Buy <ExternalLink size={12} />
                </a>
            </div>
        </div>
    </div>
);

const StoreClosed = () => (
    <div className="pt-16">
        <section className="py-32 px-4 bg-[#3D2008] min-h-[70vh] flex items-center" data-testid="store-closed">
            <div className="max-w-2xl mx-auto text-center">
                <Lock size={40} className="text-[#C9A84C] mx-auto mb-6" />
                <h1 className="font-anton text-4xl sm:text-6xl text-[#F5EDD6] uppercase leading-none mb-4">
                    The Shop Is Taking a Break
                </h1>
                <p className="font-manrope text-[#F5EDD6]/70 text-lg leading-relaxed mb-8">
                    Our merch store is temporarily closed while we restock and refresh. Check back soon — in the meantime, you can still support the build directly.
                </p>
                <a href="/donate" data-testid="store-closed-donate-btn"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#C9A84C] shadow-[4px_4px_0px_0px_rgba(201,168,76,0.5)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                    Donate to the Park Fund <ArrowRight size={16} />
                </a>
            </div>
        </section>
    </div>
);

const Merch = () => {
    const { settings, loading: settingsLoading } = useSettings();
    const [products, setProducts] = useState([]);
    const [activeCategory, setActiveCategory] = useState("All");

    useEffect(() => {
        axios.get(`${API}/merch-products`).then(r => setProducts(r.data)).catch(() => {});
    }, []);

    if (settingsLoading) return <div className="pt-16 min-h-screen bg-[#F5EDD6]" />;
    if (!settings?.store_enabled) return <StoreClosed />;

    const categories = ["All", ...Array.from(new Set(products.map(p => p.category)))];
    const filtered = activeCategory === "All" ? products : products.filter(p => p.category === activeCategory);

    return (
        <div className="pt-16">
            {/* ── HERO ── */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="merch-hero">
                <div className="max-w-4xl mx-auto">
                    <p className="font-manrope text-[#C9A84C] text-xs font-bold uppercase tracking-[0.18em] mb-5">
                        Support the Build
                    </p>
                    <h1 className="font-anton text-5xl sm:text-7xl text-[#F5EDD6] uppercase leading-none mb-4">
                        Rep the Park.<br />
                        <span className="text-[#C4522A]">Fund the Build.</span>
                    </h1>
                    <p className="font-manrope text-[#F5EDD6]/75 text-lg leading-relaxed max-w-2xl mb-8">
                        Every purchase helps fund a free, modern concrete skatepark in Seeley Lake. Wear the mission.
                    </p>
                    <a href={STORE_URL} target="_blank" rel="noopener noreferrer"
                        data-testid="visit-store-hero-btn"
                        className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#C9A84C] text-[#C9A84C] font-manrope font-semibold text-sm hover:bg-[#C9A84C] hover:text-[#3D2008] transition-colors duration-200">
                        Visit the Full Printify Store <ExternalLink size={14} />
                    </a>
                </div>
            </section>

            {/* ── FULFILLMENT NOTE ── */}
            <div className="bg-[#2D5A27] px-4 py-3">
                <div className="max-w-5xl mx-auto flex items-start gap-2.5">
                    <Package size={15} className="text-[#C9A84C] mt-0.5 flex-shrink-0" />
                    <p className="font-manrope text-[#F5EDD6]/80 text-sm">
                        Printed on demand and fulfilled through Printify. You'll choose your size, color, and shipping — and check out securely on their platform.
                    </p>
                </div>
            </div>

            {/* ── PRODUCTS ── */}
            <section className="py-16 px-4 bg-[#F5EDD6]" data-testid="merch-products-section">
                <div className="max-w-6xl mx-auto">
                    {products.length === 0 ? (
                        <p className="font-manrope text-center text-[#8A7A65] py-12">No products available right now.</p>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2 mb-10" data-testid="merch-filter-bar">
                                {categories.map(cat => (
                                    <button key={cat} type="button" onClick={() => setActiveCategory(cat)}
                                        data-testid={`filter-${cat.toLowerCase()}`}
                                        className={`px-4 py-2 font-manrope font-semibold text-sm border-2 transition-all duration-150 ${
                                            activeCategory === cat
                                                ? "bg-[#3D2008] text-[#F5EDD6] border-[#3D2008]"
                                                : "bg-white text-[#3D2008] border-[#D5D0C7] hover:border-[#3D2008]"
                                        }`}>
                                        {cat}
                                        {cat !== "All" && (
                                            <span className={`ml-1.5 text-xs ${activeCategory === cat ? "text-[#F5EDD6]/60" : "text-[#8A7A65]"}`}>
                                                ({products.filter(p => p.category === cat).length})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                                {filtered.map(product => <ProductCard key={product.id} product={product} />)}
                            </div>

                            <p className="font-manrope text-sm text-[#8A7A65] mt-8">
                                Showing {filtered.length} of {products.length} products
                            </p>
                        </>
                    )}
                </div>
            </section>

            {/* ── FULL STORE CTA ── */}
            <section className="py-16 px-4 bg-[#3D2008]" data-testid="merch-store-cta">
                <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-8 items-center">
                    <div>
                        <p className="font-manrope text-[#C9A84C] text-xs font-bold uppercase tracking-[0.15em] mb-3">Browse Everything</p>
                        <h2 className="font-anton text-3xl sm:text-4xl text-[#F5EDD6] uppercase mb-3">See the Full Store</h2>
                        <p className="font-manrope text-[#F5EDD6]/65 text-base leading-relaxed">
                            View all products, color options, sizing, and complete your order securely on Printify.
                        </p>
                    </div>
                    <div className="flex flex-col items-start gap-4">
                        <a href={STORE_URL} target="_blank" rel="noopener noreferrer"
                            data-testid="visit-full-store-btn"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C4522A] text-white font-manrope font-bold text-sm border-2 border-[#C9A84C] hover:bg-[#C4522A]/85 transition-colors duration-200">
                            <ShoppingBag size={16} />
                            Shop at seeley-lake-skate-park.printify.me
                        </a>
                        <p className="font-manrope text-[#F5EDD6]/40 text-xs">
                            Opens Printify in a new tab. No account needed to browse.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── DONATION TIE-IN ── */}
            <section className="py-12 px-4 bg-[#C4522A]">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
                    <div>
                        <p className="font-manrope text-white/80 text-sm mb-1">Want to give directly?</p>
                        <h3 className="font-anton text-2xl text-white uppercase">Make a Direct Donation</h3>
                    </div>
                    <a href="/donate" data-testid="merch-to-donate-link"
                        className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#C4522A] font-manrope font-bold text-sm hover:bg-[#F5EDD6] transition-colors duration-200">
                        Donate to the Park Fund <ArrowRight size={14} />
                    </a>
                </div>
            </section>
        </div>
    );
};

export default Merch;
